import { api } from '@sentinel/web-core';
import type {
  ManualPosition,
  ManualPositionResult,
  MapFeature,
  MapFeatureCreate,
  MapFeatureUpdate,
  MapGrid,
  MapGridCreate,
  MapPositions,
  MapSector,
  MapSectorCreate,
  MapSectorUpdate,
  MissionMap,
  TeamSummary,
} from '@sentinel/shared';

const base = (missionId: string) => `/missions/${missionId}/map`;

/** The map of a mission (ADR 0020): what is drawn, the positions in view, the search grid. */
export const mappingEndpoints = {
  map: (missionId: string) => api<MissionMap>(base(missionId)),
  positions: (missionId: string, bbox: string) =>
    api<MapPositions>(`${base(missionId)}/positions?bbox=${encodeURIComponent(bbox)}`),
  createFeature: (missionId: string, body: MapFeatureCreate) =>
    api<MapFeature>(`${base(missionId)}/features`, { method: 'POST', body }),
  updateFeature: (missionId: string, featureId: string, body: MapFeatureUpdate) =>
    api<MapFeature>(`${base(missionId)}/features/${featureId}`, { method: 'PATCH', body }),
  deleteFeature: (missionId: string, featureId: string) =>
    api<MapFeature>(`${base(missionId)}/features/${featureId}`, { method: 'DELETE' }),
  restoreFeature: (missionId: string, featureId: string) =>
    api<MapFeature>(`${base(missionId)}/features/${featureId}/restore`, { method: 'POST' }),
  createSector: (missionId: string, body: MapSectorCreate) =>
    api<MapSector>(`${base(missionId)}/sectors`, { method: 'POST', body }),
  updateSector: (missionId: string, sectorId: string, body: MapSectorUpdate) =>
    api<MapSector>(`${base(missionId)}/sectors/${sectorId}`, { method: 'PATCH', body }),
  deleteSector: (missionId: string, sectorId: string) =>
    api<MapSector>(`${base(missionId)}/sectors/${sectorId}`, { method: 'DELETE' }),
  restoreSector: (missionId: string, sectorId: string) =>
    api<MapSector>(`${base(missionId)}/sectors/${sectorId}/restore`, { method: 'POST' }),
  createGrid: (missionId: string, body: MapGridCreate) =>
    api<MapGrid>(`${base(missionId)}/grid`, { method: 'POST', body }),
  removeGrid: (missionId: string) => api<MapGrid>(`${base(missionId)}/grid`, { method: 'DELETE' }),
  restoreGrid: (missionId: string, gridId: string) =>
    api<MapGrid>(`${base(missionId)}/grids/${gridId}/restore`, { method: 'POST' }),
  /** The radio channel of a team, shown on the map sheet; null: the mission's. */
  setTeamRadio: (missionId: string, teamId: string, radioChannel: string | null) =>
    api<TeamSummary>(`/missions/${missionId}/teams/${teamId}/radio`, {
      method: 'PATCH',
      body: { radioChannel },
    }),
  /** A team placed by hand from what the radio said (no tracker, or a silent one). */
  placeTeam: (missionId: string, teamId: string, body: ManualPosition) =>
    api<ManualPositionResult>(`/missions/${missionId}/teams/${teamId}/position`, {
      method: 'PUT',
      body,
    }),
};
